// ─────────────────────────────────────────────────────────────
// ProfileSettings
//
// Cleanup pass: visual hierarchy + section grouping.
// - Save Changes is the only primary CTA on the page.
// - Trading Rules / Export / Reset are visually de-prioritized
//   (secondary, utility, danger) so the eye finds the primary
//   action immediately.
// - "FORCE LOGOUT" demoted to a small text link at the bottom
//   so it stays accessible for debugging without screaming.
// - Appearance section: dashboard layout toggle (Vertical / Horizontal)
//   persisted to profiles.dashboard_layout via updateProfile.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { InstrumentsManager } from "../../components/InstrumentsManager";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { ExportTradesModal } from "../../components/ui/ExportTradesModal";
import { DeleteAccountModal } from "./DeleteAccountModal";
import { C } from "../../lib/colors";

// Small uppercase section divider — used between card groups
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10,
      color: C.muted,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      fontWeight: 600,
      marginTop: 18,
      marginBottom: 8,
      paddingLeft: 4,
    }}>
      {children}
    </div>
  );
}

// Secondary (non-primary) button — outlined, muted. Use for navigation/utility.
function SecondaryBtn({ onClick, disabled, children, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "11px 14px",
        background: "transparent",
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        color: disabled ? C.muted : C.text,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 0.15s",
        ...style,
      }}
      onMouseEnter={e => !disabled && (e.currentTarget.style.background = C.border + "40")}
      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

// Layout option card — used inside the dashboard layout selector
function LayoutOption({ value, current, onSelect, label, description, preview }) {
  const isActive = current === value;
  return (
    <button
      onClick={() => onSelect(value)}
      style={{
        flex: 1,
        padding: "12px 10px",
        background: isActive ? C.blue + "15" : "transparent",
        border: `1px solid ${isActive ? C.blue : C.border}`,
        borderRadius: 8,
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.15s",
      }}
    >
      {/* Mini preview illustration */}
      <div style={{
        width: "100%",
        height: 48,
        background: "#0f1322",
        borderRadius: 4,
        marginBottom: 8,
        overflow: "hidden",
        display: "flex",
        gap: 3,
        padding: 4,
        boxSizing: "border-box",
      }}>
        {preview}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? C.blue : C.text, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
        {description}
      </div>
      {isActive && (
        <div style={{
          marginTop: 6,
          fontSize: 10,
          color: C.blue,
          fontWeight: 600,
        }}>
          ✓ Active
        </div>
      )}
    </button>
  );
}

export function ProfileSettings({ profile, updateProfile, signOut, setView, supabase, userId, accounts, currentAccountId, trades = [] }) {
  const navigate = useNavigate();
  const [name, setName]           = useState(profile?.display_name || "");
  const [saved, setSaved]         = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Dashboard layout — read from profile, default to vertical
  const [dashLayout, setDashLayout] = useState(profile?.dashboard_layout || (window.innerWidth >= 1024 ? "horizontal" : "vertical"));
  const [layoutSaved, setLayoutSaved] = useState(false);

  const handleAccountDeleted = async () => {
    try { localStorage.clear(); } catch {}
    await signOut?.();
    window.location.href = "/?account_deleted=1";
  };

  const handleSave = async () => {
    await updateProfile({ display_name: name });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLayoutSelect = (value) => {
    // Optimistic update — set local state immediately so the UI reflects
    // the choice without waiting for a profile refresh (which would
    // momentarily null out profile and trigger the onboarding wizard).
    setDashLayout(value);
    setLayoutSaved(true);
    setTimeout(() => setLayoutSaved(false), 2000);
    // Fire-and-forget — no await, no refreshProfile side effect
    updateProfile({ dashboard_layout: value }).catch(err =>
      console.error("Failed to save dashboard layout:", err)
    );
  };

  const forceLogout = () => {
    if (!window.confirm("Reset the app? This will clear local data and reload.")) return;
    localStorage.clear();
    window.location.reload(true);
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: 20 }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => setView("dashboard")}
          style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, padding: 0 }}
        >
          ‹ Dashboard
        </button>
        <h2 style={{ margin: "8px 0 4px", fontSize: 20, fontWeight: 800 }}>Profile Settings</h2>
        <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>Manage your account</p>
      </div>

      {/* ── ACCOUNT IDENTITY + Display Name form ─────────────────────
          Primary action of this page: Save Changes. The only green
          button on the screen, so the eye lands here first.        */}
      <SectionLabel>Account</SectionLabel>
      <Card style={{ marginBottom: 6 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          marginBottom: 20, paddingBottom: 18,
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: `linear-gradient(135deg, ${C.blue}, ${C.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, fontWeight: 800, color: "#fff", flexShrink: 0,
          }}>
            {(profile?.display_name || "?")[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              {profile?.display_name || "Trader"}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {profile?.email || ""}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{
              fontSize: 10, color: C.muted, display: "block", marginBottom: 6,
              textTransform: "uppercase", letterSpacing: "0.1em",
            }}>
              Display Name
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              style={{
                width: "100%",
                background: "#1a1d2e",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "10px 12px",
                color: C.text, fontSize: 14, outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <Btn onClick={handleSave} disabled={!name.trim() || saved} style={{ width: "100%" }}>
            {saved ? "✓ Saved!" : "Save Changes"}
          </Btn>
        </div>
      </Card>

      {/* ── APPEARANCE — Dashboard Layout ────────────────────────────
          Persisted to profiles.dashboard_layout.
          Requires migration: alter table profiles add column
          dashboard_layout text default 'vertical'
          check (dashboard_layout in ('vertical','horizontal'));   */}
      <SectionLabel>Appearance</SectionLabel>
      <Card style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            Dashboard Layout
          </div>
          {layoutSaved && (
            <div style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>✓ Saved!</div>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Choose how your dashboard is arranged. Horizontal layout puts everything
          side by side so you can see more at once without scrolling.
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          {/* Vertical option */}
          <LayoutOption
            value="vertical"
            current={dashLayout}
            onSelect={handleLayoutSelect}
            label="Vertical"
            description="Stacked sections, scroll to explore"
            preview={
              // Mini mockup: single tall column of blocks
              <div style={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                <div style={{ height: 6, background: "#1e2a3a", borderRadius: 2, width: "100%" }} />
                <div style={{ height: 10, background: "#1a2235", borderRadius: 2, width: "100%" }} />
                <div style={{ height: 8, background: "#1e2a3a", borderRadius: 2, width: "100%" }} />
                <div style={{ height: 6, background: "#1a2235", borderRadius: 2, width: "100%" }} />
                <div style={{ height: 8, background: "#1e2a3a", borderRadius: 2, width: "100%" }} />
              </div>
            }
          />

          {/* Horizontal option */}
          <LayoutOption
            value="horizontal"
            current={dashLayout}
            onSelect={handleLayoutSelect}
            label="Horizontal"
            description="Three-column layout, everything above the fold"
            preview={
              // Mini mockup: three columns side by side
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, width: "28%" }}>
                  <div style={{ height: 8, background: "#1e3a2a", borderRadius: 2 }} />
                  <div style={{ height: 14, background: "#1a2e1f", borderRadius: 2 }} />
                  <div style={{ height: 10, background: "#1e3a2a", borderRadius: 2 }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                  <div style={{ height: 6, background: "#1e2a3a", borderRadius: 2 }} />
                  <div style={{ height: 18, background: "#1a2235", borderRadius: 2 }} />
                  <div style={{ height: 12, background: "#1e2a3a", borderRadius: 2 }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, width: "28%" }}>
                  <div style={{ height: 10, background: "#2a1e1e", borderRadius: 2 }} />
                  <div style={{ height: 12, background: "#231a1a", borderRadius: 2 }} />
                  <div style={{ height: 8, background: "#2a1e1e", borderRadius: 2 }} />
                </div>
              </>
            }
          />
        </div>
      </Card>

      {/* ── TRADING — Rules (navigation, secondary) ─────────────────── */}
      <SectionLabel>Trading</SectionLabel>
      <Card style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Trading Rules
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Configure the rules you want to trade by — daily caps, time blackouts,
          news avoidance. Rules are per-account, so each combine, eval, or funded
          account can have its own discipline.
        </div>
        <SecondaryBtn onClick={() => navigate("/settings/rules")}>
          🛡️ Manage Trading Rules
        </SecondaryBtn>
      </Card>

      {/* ── DATA — Export (utility, secondary) ──────────────────────── */}
      <SectionLabel>Data</SectionLabel>
      <Card style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Data Management
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Download your trades for backup, taxes, or moving to another tool.
          The export is re-importable into FUTURES.OS.
        </div>
        <SecondaryBtn
          onClick={() => setExportOpen(true)}
          disabled={trades.length === 0}
        >
          {trades.length === 0
            ? "No trades to export"
            : `⬇ Export ${trades.length} trade${trades.length === 1 ? "" : "s"} to CSV`}
        </SecondaryBtn>
      </Card>

      {/* ── Per-account instrument manager (only when an account is active) */}
      {currentAccountId && supabase && userId && (
        <div style={{ marginTop: 6 }}>
          <InstrumentsManager
            currentAccountId={currentAccountId}
            supabase={supabase}
            userId={userId}
          />
        </div>
      )}

      {/* ── SESSION — Sign Out (danger, primary of its section) ─────── */}
      <SectionLabel>Session</SectionLabel>
      <Card style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Sign Out
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
          You'll need to log back in to access your data.
        </div>
        <Btn variant="danger" onClick={signOut} style={{ width: "100%" }}>
          Sign Out
        </Btn>
      </Card>

      {/* ── Reset-app text link (formerly the FORCE LOGOUT banner) ────
          Demoted to a small text link so it stays accessible for
          debugging without dominating the page. */}
      <div style={{ textAlign: "center", marginTop: 18, marginBottom: 24 }}>
        <button
          onClick={forceLogout}
          style={{
            background: "none",
            border: "none",
            color: C.red,
            fontSize: 11,
            cursor: "pointer",
            textDecoration: "underline",
            padding: "4px 8px",
            opacity: 0.7,
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
          onMouseLeave={e => (e.currentTarget.style.opacity = 0.7)}
        >
          Reset app & clear local data
        </button>
      </div>

      {/* ── DANGER ZONE — Delete Account ──────────────────────────────
          Permanent account deletion with 30-day grace period. Stripe
          subscription cancels at period end. */}
      <div style={{
        fontSize: 10, color: C.red, textTransform: "uppercase",
        letterSpacing: "0.12em", fontWeight: 600,
        marginTop: 18, marginBottom: 8, paddingLeft: 4,
      }}>
        Danger Zone
      </div>
      <Card style={{ marginBottom: 24, border: `1px solid ${C.red}30` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Delete account
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Permanently delete your account and all data. Your subscription will
          cancel at the end of the current billing period. You can restore your
          account by signing back in within 30 days.
        </div>
        <button
          onClick={() => setDeleteOpen(true)}
          style={{
            padding: "10px 18px",
            background: "transparent",
            border: `1px solid ${C.red}`,
            borderRadius: 8,
            color: C.red,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = C.red + "10")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          Delete my account
        </button>
      </Card>

      <ExportTradesModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        trades={trades}
        accounts={accounts}
      />

      <DeleteAccountModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        profile={profile}
        onDeleted={handleAccountDeleted}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DISCIPLINE ANALYTICS
// ─────────────────────────────────────────────────────────────
